import React, {
    useState,
    useCallback,
    useMemo,
    lazy,
    Suspense,
    useEffect,
} from 'react';
import type { Theme } from '@material-ui/core';
import { Box, TextField } from '@material-ui/core';
import { ActionButton } from '@klp/design';
import type { FormField } from '@klp/formgenerator/interfaces';
import { useGetVurderingsskjemaQuery } from '../../services/rtkApi';
import { Loader } from '@klp/design/Loader';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
const VurderingsskjemaModal = lazy(() =>
    import('./VurderingsskjemaModal').then((mod) => ({
        default: mod.VurderingsskjemaModal,
    })),
);
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';

type VurderingsskjemaProps = {
    handleClosePopper?: () => void;
    prosessInstansId: string;
    field: FormField;
    isRequired: boolean;
    isReadOnly?: boolean;
};

const styles = (theme: Theme) => ({
    infoContainer: {
        display: 'flex',
        alignItems: 'center',

        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'space-around',
        },
    },
    oversiktField: {
        '& .MuiInputBase-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
        marginRight: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            marginBottom: theme.spacing(2),
            marginRight: 0,
        },
    },
});

export const Vurderingsskjema = ({
    prosessInstansId,
    field,
    isReadOnly,
    isRequired,
}: VurderingsskjemaProps) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);

    const [openModal, setOpenModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);

    // Update formik field in the parent
    const { setFieldValue } = useFormikContext<Record<string, unknown>>();

    const handleSaveComplete = useCallback(
        (sumTotal: string | number) => {
            if (!field) return;
            const fieldValue =
                field.type === 'string' ? String(sumTotal) : sumTotal;
            setFieldValue(field.id as string, fieldValue);
        },
        [field, setFieldValue],
    );
    const {
        data: vurderingsskjemaData,
        isFetching: vurderingsskjemaIsFetching,
        isLoading: vurderingsskjemaIsLoading,
    } = useGetVurderingsskjemaQuery(
        {
            prosessInstansId,
        },
        { skip: !prosessInstansId },
    );

    const handleOpen = useCallback(() => setOpenModal(true), []);
    const handleClose = useCallback(() => setOpenModal(false), []);

    const numericValue = useMemo(
        () => vurderingsskjemaData?.vurderingsoppsummering?.sumTotal,
        [vurderingsskjemaData],
    );

    return (
        <Loader
            isLoading={vurderingsskjemaIsLoading || vurderingsskjemaIsFetching}
        >
            <Box className={classes.infoContainer}>
                <NumericFormat
                    className={classes.oversiktField}
                    thousandSeparator={' '}
                    decimalScale={2}
                    decimalSeparator=","
                    value={numericValue}
                    customInput={TextField}
                    variant="outlined"
                    id={field?.id}
                    label={t('prosjekt.vurderingsskjema.totalOversikt')}
                    required={isRequired}
                    disabled={true}
                    size="small"
                />

                <ActionButton
                    actionType="forward"
                    translation="prosjekt.vurderingsskjema.open"
                    onClick={handleOpen}
                    data-cy="VurderingsskjemaView_AddVurderinger"
                    isLoading={modalLoading}
                />
            </Box>

            {openModal && (
                <Suspense
                    fallback={<ModalLoadIndicator onChange={setModalLoading} />}
                >
                    <VurderingsskjemaModal
                        isReadOnly={isReadOnly || false}
                        isRequired={isRequired}
                        prosessInstansId={prosessInstansId}
                        onClose={handleClose}
                        isOpen={openModal}
                        onSaveComplete={handleSaveComplete}
                    />
                </Suspense>
            )}
        </Loader>
    );
};

function ModalLoadIndicator({ onChange }: { onChange: (v: boolean) => void }) {
    useEffect(() => {
        onChange(true);
        return () => onChange(false);
    }, [onChange]);
    // render nothing visible; test can still query by testid if needed
    return (
        <div
            data-testid="vurderingsskjema-modal-loading"
            style={{ display: 'none' }}
        />
    );
}
